<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import GbButton from '@/components/GbButton.vue'
import { useUserStore } from '@/authentication/user.store'
import PrintLayoutContainer from './PrintLayoutContainer.vue'
import PrintPage from './PrintPage.vue'
import PrintPreparing from './PrintPreparing.vue'
import PrintPreviewOverlay from './PrintPreviewOverlay.vue'
import { usePrintPreview } from './usePrintPreview'

type Printer = { name?: string, email?: string }

defineProps<{
  studentName: string
}>()

const emit = defineEmits<{
  close: []
  print: []
}>()

defineSlots<{
  default(): unknown
}>()

const { user } = storeToRefs(useUserStore())
const { isTransitioning, isTrial, printQuota, printsRemaining, printAndClose } = usePrintPreview(close)

// The user store's state is untyped JavaScript.
const printer = computed(() => user.value as Printer)
const printedAt = new Date().toISOString().split('T').join(' ')

function close() {
  emit('close')
}

function printPage() {
  printAndClose(() => emit('print'))
}
</script>

<template>
  <PrintPreviewOverlay :is-initializing="isTransitioning" label="Print preview" @close="close">
    <PrintPreparing v-if="isTransitioning" />

    <PrintLayoutContainer v-else>
      <template #top>
        <div class="container flex">
          <p v-if="isTrial" class="flex-auto pt-1.5 text-right text-gray-700" data-testid="printable-quota">
            {{ printsRemaining }} of {{ printQuota }} prints remaining this month
          </p>
          <div class="ml-auto flex flex-none flex-col gap-2 pl-3">
            <GbButton type="button" gb-theme="bluePrimary" class="w-full justify-center" data-testid="printable-print" @click="printPage">
              <span class="material-icons-round mr-1 text-text-lg" aria-hidden="true">print</span>
              Print
            </GbButton>
            <GbButton type="button" gb-theme="grayOutlineSecondary" class="w-full justify-center" data-testid="printable-cancel" @click="close">
              <span class="material-icons-round mr-1 text-text-lg" aria-hidden="true">close</span>
              Cancel
            </GbButton>
          </div>
        </div>
      </template>

      <template #body>
        <PrintPage class="printed-saved-page text-text-md [&_p]:max-w-none print:[&_a]:text-black print:[&_blockquote]:border-0 print:[&_a_.material-icons-round]:hidden">
          <p class="text-text-sm leading-none" data-testid="printable-byline">
            Printed by <b>{{ printer.name }} ({{ printer.email }})</b> from folder <b class="fs-exclude">{{ studentName }}</b> on {{ printedAt }}
          </p>
          <p v-if="isTrial" class="text-text-sm leading-none text-gray-700 print:text-black" data-testid="printable-terms">
            Per our legal Terms of Use, the print feature is intended to facilitate communication and collaboration, and not to be used for
            illicit saving or distribution of proprietary content. <b>{{ printsRemaining }} of {{ printQuota }} prints remaining</b> for this month.
          </p>
          <slot />
        </PrintPage>
      </template>
    </PrintLayoutContainer>
  </PrintPreviewOverlay>
</template>

<style scoped>
/* Bootstrap's print rules printed each external link's address after it on Vue 2; strategy links carry an aria-label and printed none. */
@media print {
  .printed-saved-page :deep(a[href]:not([aria-label]))::after {
    content: " (" attr(href) ")";
  }
}
</style>
